/**
 * @fileoverview Modal for patient status change with justification
 * Uses DmsStatusChangeModal for consistent behavior across the application
 */
import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { intl } from "dmsbasereact";
import { DmsStatusChangeModal } from "../DmsStatusChangeModal";
import { PatientStatusEnum, PatientStatus } from "../../constants/PatientStatusEnum";

/**
 * Build payload for patient status toggle
 *
 * Always sends the predefined reason pk (including for "Other"), so reports
 * and queries can categorize inactivations correctly. The custom text is sent
 * in status_reason when "Other" is selected.
 */
const buildPatientPayload = () => (reasonPk, customReason, newStatus) => ({
  is_active: newStatus === PatientStatusEnum.ACTIVE,
  patient_status_reason_pk: reasonPk ? Number(reasonPk) : null,
  status_reason: customReason || null,
});

/**
 * Modal for changing patient status with justification
 */
export const ModalPatientReasonStatus = ({
  isVisible,
  onClose,
  patientData = null,
  table = null}) => {
  // Determine current and new status
  const currentStatus = patientData?.status === PatientStatusEnum.ACTIVE
    ? PatientStatusEnum.INACTIVE  // If changing to inactive, current was active
    : PatientStatusEnum.ACTIVE;   // If changing to active, current was inactive
  const newStatus = patientData?.status;

  const handleSuccess = useCallback(() => {
    table?.current?.updateTable();
  }, [table]);

  if (!patientData) return null;

  return (
    <DmsStatusChangeModal
      open={isVisible}
      onClose={onClose}
      entityPk={patientData.pk}
      entityName={patientData.name}
      currentStatus={currentStatus}
      newStatus={newStatus}
      apiEndpoint="/patient"
      apiPathSuffix="/toggle_status"
      buildPayload={buildPatientPayload()}
      onSuccess={handleSuccess}
      title={intl.get("PatientStatusChange").d("Patient Status Change")}
      statusLabels={{
        [PatientStatusEnum.ACTIVE]: PatientStatus.getText(PatientStatusEnum.ACTIVE),
        [PatientStatusEnum.INACTIVE]: PatientStatus.getText(PatientStatusEnum.INACTIVE),
      }}
      reasonLabel={intl.get("StatusJustification").d("Status Justification")}
      reasonRequired={true}
      reasonType="patient_status_reason"
    />
  );
};

ModalPatientReasonStatus.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patientData: PropTypes.object,
  table: PropTypes.object,
};

